import { ComponentChildren } from 'preact';
import type {
  States,
  LinkRendererContext,
  DiagramLinkData,
  DiagramLayoutOutput,
  Bounds,
  PromotedLinkId
} from './diagram.types';
import { layerStyles, linkLayerStyle } from './themes/DiagramStyles.css';
import { getZoomToFitZoom } from './utils/zoomUtils';
import { DiagramPromotedLinkElement } from './DiagramPromotedLinkElement';

type Props<K2, K1, D2, L> = {
  links: PromotedLinkId<K1, D2>[];
  states: States<K1, K2, D2>;
  linkRenderer: (context: LinkRendererContext<K1, D2, L>) => ComponentChildren;
  width: number;
  height: number;
  supportsSelection: boolean;
  layoutOutput: L;
  combinedNodeBounds: Bounds;
  onLinkSizeChanged: (width: number, height: number, id: string) => void;
  minX: number;
  minY: number;
};

export function DiagramPromotedLinkLayer<
  K2 extends string | number,
  K1 extends string | number,
  D2 extends DiagramLinkData<K2, K1> = DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2> = DiagramLayoutOutput<K1, K2>
>({
  links,
  states,
  linkRenderer,
  width,
  height,
  supportsSelection,
  onLinkSizeChanged,
  layoutOutput,
  combinedNodeBounds,
  minX,
  minY
}: Props<K2, K1, D2, L>) {
  const zoom = getZoomToFitZoom(width, height, combinedNodeBounds);
  return (
    <div class={linkLayerStyle} style={{ top: -minY, left: -minX }}>
      <svg width={width / zoom} height={height / zoom} class={layerStyles} overflow="visible">
        {links.map((link) => {
          const state = {
            isSelected: states.selectedPromotedLinkIds.includes(link),
            isFocused: states.focusedId === link,
            isHovered: states.hoveredId === link,
            zoom: states.zoom
          };
          return (
            <DiagramPromotedLinkElement
              id={link}
              state={state}
              isDimmed={
                (states.highlightedNodeIds.length !== 0 ||
                  states.highlightedLinkIds.length !== 0 ||
                  states.highlightedPromotedLinkIds.length !== 0) &&
                !states.highlightedPromotedLinkIds.includes(link)
              }
              activeId={states.activeId}
              linkRenderer={linkRenderer}
              supportsSelection={supportsSelection}
              layoutOutput={layoutOutput}
              onLinkSizeChanged={onLinkSizeChanged}
            />
          );
        })}
      </svg>
    </div>
  );
}
